import React from "react";

import { styled } from "@mui/material";
import Stack from "@mui/material/Stack";
import { CharacterCountView } from "@src/components/RichTextEditor/CharacterCountView";
import { FontSize } from "@src/components/RichTextEditor/Extensions/FontSize";
import { MenuBar } from "@src/components/RichTextEditor/MenuBar";
import { useRichEditorState } from "@src/components/RichTextEditor/Util/useRichEditorState";
import { stripHTML } from "@src/utils/stripHTML";
import { CharacterCount } from "@tiptap/extension-character-count";
import { Color } from "@tiptap/extension-color";
import { Placeholder } from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

interface RichTextEditorProps {
  value?: string;
  stripped?: string;
  placeholder?: string;
  count?: number;
  limit?: number;
  onChange?: (value: string) => void;
  disabled: boolean;
  minHeight?: string;
}

export const RichTextEditor = ({
  value,
  stripped,
  limit,
  placeholder,
  onChange,
  disabled,
  minHeight,
}: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: { HTMLAttributes: { class: "ig-rte" } },
      }),
      Underline,
      TextStyle,
      Color,
      FontSize,
      CharacterCount.configure({
        limit,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    editable: !disabled,
    content: value,
    onUpdate: (props) => {
      const raw = props.editor.getHTML();
      const stripped = stripHTML(raw);
      onChange?.(stripped ? raw : "");
    },
  });
  const state = useRichEditorState(editor);

  if (editor === null) {
    return null;
  }

  return (
    <StyledStack direction={"column"} alignItems={"stretch"}>
      <MenuBar editor={editor} state={state} disabled={disabled} />
      <EditorContent
        editor={editor}
        placeholder={placeholder}
        style={minHeight ? { minHeight } : undefined}
      />
      <CharacterCountView stripped={stripped} limit={limit} />
    </StyledStack>
  );
};

const StyledStack = styled(Stack)(({ theme }) => ({
  border: `1px solid ${theme.igBorders.core.default}`,
  "& .tiptap": {
    fontSize: "14px",
    padding: "10px",
    "&.ProseMirror-focused": {
      outline: "none",
    },
    "& p": {
      margin: "0px",
    },
  },
}));
